import {
  BarChart3,
  Bot,
  Boxes,
  Car,
  DoorOpen,
  Globe,
  IdCard,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Sun,
  UtensilsCrossed,
  Users,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PermissionCode } from '@amai/contracts';

/**
 * Sidebar do AMAI OPS.
 *
 * Módulos ainda não construídos aparecem desabilitados com o selo "em breve":
 * o operador enxerga o mapa inteiro do sistema desde o primeiro dia e sabe o
 * que já pode usar. `children` são as seções que abrem dentro da barra.
 */
export interface NavChild {
  label: string;
  to: string;
  /** Uma permissão, ou várias — aí TODAS são exigidas. */
  permission?: PermissionCode | PermissionCode[];
  soon?: boolean;
}

/** Uma tela que junta dois módulos exige a permissão dos dois. */
export function canOpen(
  can: (code: PermissionCode) => boolean,
  permission: PermissionCode | PermissionCode[] | undefined,
): boolean {
  if (!permission) return true;
  return Array.isArray(permission) ? permission.every(can) : can(permission);
}

/**
 * Os grupos do menu, na ordem em que aparecem.
 *
 * O menu tinha dez módulos numa lista só. Agrupar é o que deixa achar o
 * módulo pela NATUREZA do trabalho: quem cuida da obra procura em Operação,
 * quem cuida do dinheiro em Administrativo. Os grupos saem dos módulos que
 * existem — nenhum foi criado para completar a lista.
 */
export const NAV_GROUPS = {
  operacao: 'Operação',
  comercial: 'Comercial',
  administrativo: 'Administrativo',
  sistema: 'Sistema',
} as const;
export type NavGroup = keyof typeof NAV_GROUPS;

export interface NavModule {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Sem grupo, o módulo fica no topo (a Visão Geral). */
  group?: NavGroup;
  to?: string;
  /** Módulo previsto porém ainda não construído (milestones seguintes). */
  soon?: boolean;
  children?: NavChild[];
}

export const MODULES: NavModule[] = [
  { id: 'visao', label: 'Visão Geral', icon: LayoutDashboard, to: '/' },
  {
    id: 'compras',
    group: 'operacao',
    label: 'Compras',
    icon: ShoppingCart,
    children: [
      { label: 'Requisições', to: '/compras/requisicoes', permission: 'requisicoes:ver' },
      { label: 'Cotações', to: '/compras/cotacoes', permission: 'cotacoes:ver' },
      { label: 'Pedidos', to: '/compras/pedidos', permission: 'pedidos-compra:ver' },
      { label: 'Recebimentos', to: '/compras/recebimentos', permission: 'recebimentos:ver' },
      {
        label: 'Notas de entrada',
        to: '/compras/documentos',
        permission: 'documentos-fiscais:ver',
      },
    ],
  },
  {
    id: 'estoque',
    group: 'operacao',
    label: 'Estoque',
    icon: Boxes,
    children: [
      { label: 'Itens e materiais', to: '/estoque/itens', permission: 'itens:ver' },
      { label: 'Saldos', to: '/estoque/saldos', permission: 'movimentacoes:ver' },
      { label: 'Validades', to: '/estoque/validades', permission: 'movimentacoes:ver' },
      { label: 'Movimentações', to: '/estoque/movimentacoes', permission: 'movimentacoes:ver' },
      { label: 'Inventário', to: '/estoque/inventario', permission: 'inventarios:ver' },
      { label: 'Consumo por centro de custo', to: '/estoque/consumo', permission: 'movimentacoes:ver' },
      { label: 'Locais', to: '/estoque/locais', permission: 'locais-estoque:ver' },
    ],
  },
  {
    id: 'associados',
    group: 'comercial',
    label: 'Associados',
    icon: ShieldCheck,
    /**
     * Aberto de volta a pedido do Diretor (19/09/2026).
     *
     * Ficou escondido enquanto era só cadastro. Agora o M5 está inteiro aqui
     * dentro — mensalidade que nasce sozinha, cobrança automática e régua de
     * inadimplência —, e módulo que ninguém consegue abrir é trabalho que o
     * Diretor recebe "pela fé": foram quatro entregas que ele não pôde ver.
     */
    children: [
      { label: 'Associados', to: '/clube/associados', permission: 'associados:ver' },
      { label: 'Planos & Lotes', to: '/clube/planos', permission: 'planos-socios:ver' },
      { label: 'Painel de vendas', to: '/clube/painel-vendas', permission: 'associados:ver' },
      { label: 'Mensalidades', to: '/clube/mensalidades', permission: 'mensalidades:ver' },
      { label: 'Inadimplência', to: '/clube/inadimplencia', permission: 'inadimplencia:ver' },
      { label: 'Pessoas restritas', to: '/clube/restritos', permission: 'restritos:ver' },
    ],
  },
  {
    id: 'agentes',
    group: 'comercial',
    label: 'Agentes',
    icon: Bot,
    children: [
      { label: 'Canal', to: '/agentes/canal', permission: 'canal-mensagens:ver' },
      { label: 'Mensagens', to: '/agentes/mensagens', permission: 'mensagens:ver' },
    ],
  },
  {
    id: 'financeiro',
    group: 'administrativo',
    label: 'Financeiro',
    icon: Wallet,
    children: [
      { label: 'Contas a pagar', to: '/financeiro/contas-pagar', permission: 'lancamentos:ver' },
      {
        label: 'Contas a receber',
        to: '/financeiro/contas-receber',
        permission: 'lancamentos:ver',
      },
      { label: 'Cobranças', to: '/financeiro/cobrancas', permission: 'cobrancas:ver' },
      // Fornecedor mora no Financeiro porque é de lá que se olha para ele:
      // quanto já se gastou, o que ainda se deve, o que vence esta semana.
      {
        label: 'Fornecedores',
        to: '/financeiro/fornecedores',
        // A lista é de Pessoas e o painel é de lançamentos: sem os dois, a
        // tela abre e quebra na primeira chamada.
        permission: ['pessoas:ver', 'lancamentos:ver'],
      },
      { label: 'Agenda de vencimentos', to: '/financeiro/agenda', permission: 'lancamentos:ver' },
      { label: 'Fluxo de caixa', to: '/financeiro/fluxo-caixa', permission: 'lancamentos:ver' },
      { label: 'DRE — resultado', to: '/financeiro/dre', permission: 'lancamentos:ver' },
      { label: 'Custo por centro de custo', to: '/financeiro/custo-por-centro', permission: 'lancamentos:ver' },
      { label: 'Contas e caixas', to: '/financeiro/contas', permission: 'contas-bancarias:ver' },
      {
        label: 'Centros de custo',
        to: '/financeiro/centros-custo',
        permission: 'centros-custo:ver',
      },
      { label: 'Plano de contas', to: '/financeiro/plano-contas', permission: 'plano-contas:ver' },
    ],
  },
  {
    id: 'rh',
    group: 'administrativo',
    label: 'RH',
    // Crachá, não capacete: o capacete é da Obra. Com a barra recolhida o
    // ícone é TUDO o que se vê — dois módulos com o mesmo desenho viram
    // adivinhação de qual é qual.
    icon: IdCard,
    children: [
      { label: 'Colaboradores', to: '/rh/colaboradores', permission: 'colaboradores:ver' },
      { label: 'Férias e afastamentos', to: '/rh/afastamentos', permission: 'afastamentos:ver' },
      { label: 'Folha de pagamento', to: '/rh/folha', permission: 'folha-pagamento:ver' },
      { label: 'Departamentos e cargos', to: '/rh/cargos', permission: 'departamentos:ver' },
    ],
  },
  {
    id: 'cadastros',
    group: 'administrativo',
    label: 'Cadastros',
    icon: Users,
    children: [
      { label: 'Pessoas', to: '/cadastros/pessoas', permission: 'pessoas:ver' },
      { label: 'Categorias', to: '/cadastros/categorias', soon: true },
    ],
  },
  {
    id: 'configuracoes',
    group: 'sistema',
    label: 'Configurações',
    icon: Settings,
    to: '/configuracoes',
    children: [
      { label: 'Empresa e unidades', to: '/configuracoes', permission: 'empresas:ver' },
      { label: 'Usuários', to: '/configuracoes/usuarios', permission: 'usuarios:ver' },
      { label: 'Perfis e permissões', to: '/configuracoes/perfis', permission: 'perfis:ver' },
      { label: 'Chaves de API', to: '/configuracoes/api-keys', permission: 'api-keys:ver' },
      {
        label: 'Certificado digital',
        to: '/configuracoes/certificado',
        permission: 'certificado-digital:ver',
      },
      {
        // A tela escolhe a conta que recebe as baixas, e a lista de contas é
        // de outro recurso: sem as duas, o item aparece no menu e a tela
        // abre sem conseguir salvar.
        label: 'Cobrança (ASAAS)',
        to: '/configuracoes/cobranca',
        permission: ['gateway-pagamento:ver', 'contas-bancarias:ver'],
      },
      { label: 'Trilha de auditoria', to: '/configuracoes/auditoria', permission: 'auditoria:ver' },
    ],
  },
  { id: 'dayuse', label: 'Day Use', icon: Sun, soon: true },
  { id: 'pdv', label: 'PDV & A&B', icon: UtensilsCrossed, soon: true },
  { id: 'acesso', label: 'Acesso', icon: DoorOpen, soon: true },
  { id: 'estacionamento', label: 'Estacionamento', icon: Car, soon: true },
  { id: 'relatorios', label: 'Relatórios', icon: BarChart3, soon: true },
  { id: 'site', label: 'Site & Conteúdo', icon: Globe, soon: true },
];

/** Encontra o módulo dono da rota atual, para acender a barra lateral. */
export function moduleForPath(pathname: string): NavModule | undefined {
  if (pathname === '/') return MODULES[0];
  return MODULES.find((module) => {
    if (module.children?.some((child) => pathname.startsWith(child.to) && child.to !== '/'))
      return true;
    return module.to && module.to !== '/' && pathname.startsWith(module.to);
  });
}

/**
 * A seção aberta, para a trilha da barra de topo.
 *
 * Casa pelo prefixo MAIS LONGO, até a barra: sem isso `/financeiro/contas`
 * responderia por `/financeiro/contas-pagar`, e `/configuracoes` (Empresa)
 * responderia por toda tela de Configurações.
 */
export function childForPath(module: NavModule | undefined, pathname: string): NavChild | undefined {
  const candidatos = (module?.children ?? []).filter(
    (child) => pathname === child.to || pathname.startsWith(`${child.to}/`),
  );
  return candidatos.sort((a, b) => b.to.length - a.to.length)[0];
}
